import { PatientBasic } from '@/fetching/sharedSchemas/basicSchemas';
import { atomWithSessionStorage } from '@/utils/browserStorage';
import { entries } from '@/utils/objects';
import { Ordering, QueryVisitRequestsArgs, VisitRequestFilter } from 'gql-types';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import xor from 'lodash/xor';
import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

import { EpisodeOfCare } from './episodeOfCare';
import { OrderPartialMap } from './lists/ordering';
import { OrderStateFilter, orderStatesToOrderStateFilter } from './order';
import { Patient } from './patient';
import { ServiceArea } from './serviceArea';
import { VisitPriority } from './visit';

/**********************
 * Visit Request Types
 *********************/
export namespace VisitRequest {
    export type Filters = VisitRequestFilter;
    export type Ordering = VisitRequestOrdering;
    export type ListVariables = QueryVisitRequestsArgs;
}

type VisitRequestOrdering = OrderPartialMap<{
    createdAt: Ordering;
    updatedAt: Ordering;
    patient: Patient.Ordering;
    episode: EpisodeOfCare.Ordering;
    serviceArea: ServiceArea.Ordering;
    tentativeDate: Ordering;
    fulfillmentWindowStartDate: Ordering;
    fulfillmentWindowEndDate: Ordering;
}>;

/**********************
 * Filter Helpers
 *********************/
const visitRequestFiltersSchema = z.object({
    patientIds: z.array(z.string()),
    territoryIds: z.array(z.string()),
    disciplineIds: z.array(z.string()),
    clinicianIds: z.array(z.string()),
    // Optional for backwards compatibility - existing stored state won't have this field
    clinicianTeamIds: z.array(z.string()).optional().default([]),
    serviceIds: z.array(z.string()),
    priorities: z.array(z.nativeEnum(VisitPriority)),
    orderState: z.optional(z.array(z.nativeEnum(OrderStateFilter)).nullable()),
});
type VisitRequestFiltersAtom = z.infer<typeof visitRequestFiltersSchema>;

export const visitRequestFiltersAtom = atomWithSessionStorage(
    'visitRequestFilters',
    {
        patientIds: [],
        territoryIds: [],
        disciplineIds: [],
        clinicianIds: [],
        clinicianTeamIds: [],
        serviceIds: [],
        priorities: [],
        orderState: [],
    },
    visitRequestFiltersSchema,
);

const DEFAULT_ORDER_STATE_FILTER = {
    isActive: { exact: true },
};

const filtersFromFiltersAtom = (filterState: VisitRequestFiltersAtom) => {
    const filters: VisitRequest.Filters = {};
    if (filterState.patientIds.length) {
        filters.patient = { id: { inList: filterState.patientIds } };
    }
    if (filterState.territoryIds.length) {
        filters.territories = { id: { inList: filterState.territoryIds } };
    }
    if (filterState.disciplineIds.length) {
        filters.disciplines = { id: { inList: filterState.disciplineIds } };
    }
    if (filterState.priorities.length) {
        filters.priority = { inList: filterState.priorities };
    }
    if (filterState.clinicianIds.length) {
        filters.assignedClinicians = { id: { inList: filterState.clinicianIds } };
    }
    if (filterState.clinicianTeamIds.length) {
        filters.assignedClinicianTeams = { id: { inList: filterState.clinicianTeamIds } };
    }
    if (filterState.serviceIds.length) {
        filters.services = { id: { inList: filterState.serviceIds } };
    }
    if (filterState.orderState?.length) {
        filters.relatedOrder = orderStatesToOrderStateFilter(filterState.orderState);
    } else {
        filters.AND = {
            relatedOrder: DEFAULT_ORDER_STATE_FILTER,
            OR: { hasOrder: { exact: false } },
        };
    }
    return filters;
};

export const useOnFilterChange = (callback: (filters: VisitRequestFiltersAtom) => void) => {
    const filterState = useAtomValue(visitRequestFiltersAtom);
    const lastFiltersRef = useRef<VisitRequestFiltersAtom>(filterState);

    useEffect(() => {
        const hasChanged = entries(filterState).some(([filterName, filterValue]) => {
            const lastFilter = lastFiltersRef.current[filterName];
            return xor(filterValue, lastFilter).length > 0;
        });

        if (hasChanged) {
            callback(filterState);
            lastFiltersRef.current = filterState;
        }
    }, [filterState, callback]);
};

export const useQueryFiltersFromFiltersAtom = () => {
    const filterState = useAtomValue(visitRequestFiltersAtom);

    const queryFilters = useMemo(() => {
        return filtersFromFiltersAtom(filterState);
    }, [filterState]);

    const numFilters = _.size(
        _.filter(filterState, (visitRequestFilterValue) =>
            visitRequestFilterValue ? visitRequestFilterValue.length > 0 : false,
        ),
    );

    return { queryFilters, numFilters };
};

/**********************
 * Visit Request Enums
 *********************/
export enum VisitRequestWorkflowStep {
    NEEDS_SERVICES = 'NEEDS_SERVICES',
    NEEDS_CLINICIAN = 'NEEDS_CLINICIAN',
    NEEDS_TENTATIVE_DATE = 'NEEDS_TENTATIVE_DATE',
    READY_FOR_PROMOTION = 'READY_FOR_PROMOTION',
    COMPLETED = 'COMPLETED',
    UNKNOWN = 'UNKNOWN',
}

export const workflowStepToInt = (step: VisitRequestWorkflowStep): number => {
    return Object.values(VisitRequestWorkflowStep).indexOf(step);
};

export function isWorkflowStepEqualOrAfter({
    currentStep,
    step,
}: {
    currentStep: VisitRequestWorkflowStep;
    step: VisitRequestWorkflowStep;
}) {
    const stepInt = workflowStepToInt(step);
    const currentStepInt = workflowStepToInt(currentStep);
    return currentStepInt >= stepInt;
}

export const renderVisitRequestWorkflowStep = (step: VisitRequestWorkflowStep): string => {
    switch (step) {
        case VisitRequestWorkflowStep.NEEDS_SERVICES:
            return 'Needs Services';
        case VisitRequestWorkflowStep.NEEDS_CLINICIAN:
            return 'Needs Clinician';
        case VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE:
            return 'Needs Date';
        case VisitRequestWorkflowStep.READY_FOR_PROMOTION:
            return 'Ready for Scheduling';
        case VisitRequestWorkflowStep.COMPLETED:
            return 'Completed';
        case VisitRequestWorkflowStep.UNKNOWN:
            return 'Unknown';
        default:
            return step;
    }
};

export const renderVisitRequestWorkflowStepAsAction = (
    step: VisitRequestWorkflowStep,
    plural = false,
): string => {
    switch (step) {
        case VisitRequestWorkflowStep.NEEDS_SERVICES:
            // Plural even when plural is false because individual appointments
            // support multiple services.
            return 'Add Services';
        case VisitRequestWorkflowStep.NEEDS_CLINICIAN:
            return plural ? 'Assign Clinicians' : 'Assign Clinician';
        case VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE:
            return plural ? 'Add Dates' : 'Add Date';
        case VisitRequestWorkflowStep.READY_FOR_PROMOTION:
            return 'Ready for Scheduling';
        case VisitRequestWorkflowStep.COMPLETED:
            return 'Completed';
        case VisitRequestWorkflowStep.UNKNOWN:
            return 'Unknown';
        default:
            return step;
    }
};

export enum VisitRequestState {
    PENDING = 'PENDING',
    FULFILLED = 'FULFILLED',
    CANCELED = 'CANCELED',
}

export const renderVisitRequestState = (state: VisitRequestState): string => {
    switch (state) {
        case VisitRequestState.PENDING:
            return 'Pending';
        case VisitRequestState.FULFILLED:
            return 'Fulfilled';
        case VisitRequestState.CANCELED:
            return 'Canceled';
        default:
            return state;
    }
};

export enum VisitRequestStateWithAll {
    PENDING = 'PENDING',
    FULFILLED = 'FULFILLED',
    CANCELED = 'CANCELED',
    All = 'All',
}

export type AppointmentForVisitRequest = {
    id: string;
    clinician: {
        id: string;
        firstName: string;
        lastName: string;
        logoUrl: string | null;
    } | null;
    services: {
        id: string;
        name: string;
    }[];
};

export type AddressForVisitRequest = {
    id: string;
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    zipCode: string;
};

export type VisitRequestPatientBasicType = {
    id: string;
    organization: {
        id: string;
        name: string;
    };
    patient: PatientBasic;
    priority: VisitPriority;
    state: VisitRequestState;
    fulfillmentWindow: {
        startDate: Date | null;
        endDate: Date | null;
    } | null;
    tentativeDate: Date | null;
    address: AddressForVisitRequest | null;
    notes: string | null;
    appointments: AppointmentForVisitRequest[];
};

export type VisitRequestDetails = {
    id: string;
    organization: {
        id: string;
        name: string;
        integrations: Record<string, string | null>;
    };
    patient: {
        id: string;
        firstName: string;
        lastName: string;
        dateOfBirth: string;
        phoneNumber: string | null;
        email: string | null;
    };
    disciplines: {
        id: string;
        name: string;
        code: string;
        displayName: string;
    }[];
    priority: VisitPriority;
    state: VisitRequestState;
    fulfillmentWindow: {
        startDate: Date | null;
        endDate: Date | null;
    } | null;
    tentativeDate: Date | null;
    address: AddressForVisitRequest | null;
    timezone: string | null;
    externalId: string | null;
    notes: string | null;
    appointments: AppointmentForVisitRequest[];
    visit: {
        id: string;
    } | null;
};
